import React, { useContext, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import Row from 'components/Common/Row';
import { V3AppsContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/V3AppsProvider';
import Typography from 'kingpin/atoms/Typography';
import Icon from 'kingpin/atoms/Icon';
import PopUpMenu from 'kingpin/atoms/PopUpMenu';
import NavMenuPopup from './NavMenuPopup';
import LeftNavItem from '../../../Common/LeftNavItem';
import PopUpMenuChevron from './PopUpMenuChevron';

const SinglePortalAppItem = ({ product }: { product: V3.Product }) => {
  const link = useMemo(() => {
    return `/apps/${product.urlSlug}`;
  }, [product.urlSlug]);

  if (!link) {
    return null;
  }

  return (
    <NavLink to={link}>
      <NavMenuPopup.ItemProductDiv>
        <Icon
          icon={product.icon}
          width={28}
          height={28}
          color={'var(--color-icon-primary)'}
        />
        <Typography.Body type={'Label 14'}>{product.label}</Typography.Body>
      </NavMenuPopup.ItemProductDiv>
    </NavLink>
  );
};

const AppItem = ({
  product,
  app,
}: {
  product: V3.Product;
  app: V3.ProductApp;
}) => {
  const link = useMemo(() => {
    return `/apps/${product.urlSlug}/${app.urlSlug}`;
  }, [app.urlSlug, product.urlSlug]);

  return (
    <NavLink to={link}>
      <NavMenuPopup.ItemDiv>
        <Typography.Body type={'Label 14'}>{app.label}</Typography.Body>
      </NavMenuPopup.ItemDiv>
    </NavLink>
  );
};

const MultiplePortalAppItem = ({
  product,
  apps,
}: {
  product: V3.Product;
  apps: V3.ProductApp[];
}) => {
  return (
    <PopUpMenu
      trigger={() => (
        <NavMenuPopup.ItemDiv style={{ justifyContent: 'space-between' }}>
          <Row centerAlign style={{ gap: 8 }}>
            <Icon
              icon={product.icon}
              width={24}
              height={24}
              color={'var(--color-icon-primary)'}
            />
            <Typography.Body type={'Label 14'}>{product.label}</Typography.Body>
          </Row>
          <Icon icon={'chevron-right'} color={'var(--color-icon-primary)'} />
        </NavMenuPopup.ItemDiv>
      )}
      position={'right-start'}
      children={() => (
        <div style={{ padding: '8px' }}>
          {apps.map((app) => (
            <AppItem app={app} product={product} key={app.id} />
          ))}
        </div>
      )}
    />
  );
};

const ProductItem = ({ product }: { product: V3.Product }) => {
  const { availableProductApps } = useContext(V3AppsContext);
  const subAppsThisUserHasAccessTo = useMemo(() => {
    return availableProductApps.filter((app) =>
      product.appIds.includes(app.id),
    );
  }, [availableProductApps, product.appIds]);

  if (subAppsThisUserHasAccessTo.length === 0) {
    return null;
  }
  if (subAppsThisUserHasAccessTo.length === 1) {
    return <SinglePortalAppItem product={product} />;
  }

  return (
    <MultiplePortalAppItem
      product={product}
      apps={subAppsThisUserHasAccessTo}
    />
  );
};

const AppsMenuPopup = () => {
  const { availableProducts } = useContext(V3AppsContext);

  const shownProducts = useMemo(() => {
    return availableProducts;
  }, [availableProducts]);

  return (
    <NavMenuPopup.PopupDiv>
      <div style={{ padding: '4px 8px 6px 8px' }}>
        <Typography.Body type={'Label xs'} color={'var(--color-text-tertiary)'}>
          FleetOps Apps
        </Typography.Body>
      </div>
      {shownProducts.map((item) => (
        <ProductItem key={item.id} product={item} />
      ))}
    </NavMenuPopup.PopupDiv>
  );
};

const AppsMenu = () => {
  const { availableProducts } = useContext(V3AppsContext);
  const { pathname } = useLocation();
  const isSelected = useMemo(() => {
    return pathname.includes('/apps/');
  }, [pathname]);

  if (availableProducts.length === 0) {
    return null;
  }

  return (
    <PopUpMenu
      trigger={({ isOpen }: { isOpen: boolean }) => (
        <LeftNavItem isOpen={isOpen || isSelected}>
          <Typography.Body type={'Label 14'}>Apps</Typography.Body>
          <PopUpMenuChevron isOpen={isOpen} />
        </LeftNavItem>
      )}
      children={() => <AppsMenuPopup />}
      position={'bottom-start'}
    />
  );
};

export default AppsMenu;
